import axios from 'axios'
import toast from '@/utils/toast'
import { getLanguage } from '@/utils/setLang'
import { TokenKey, DeviceIdKey, getCookie, LanguageKey, removeCookie, DeviceInfoKey, ClientPlatformKey } from '@/utils/cookies'
import { JSBridge } from '@/utils/jsBridge'
import i18n from '@/locales'
import { normalizeLocale } from '@/locales/localeConfig'
import { parseBigIntJson } from '@/utils/apiId'
import { brokerWebContext, isBrokerWebAuthorization, getBrowserBrokerToken, brokerWebError } from '@/utils/brokerWebAuthorization'

// 创建 Axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  transformResponse: [parseBigIntJson],
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

// getRequestLanguage 统一计算当前请求语言，/join 必须使用页面语言，其他页保留 Cookie 注入兜底。
const getRequestLanguage = () => {
  const pageLanguage = normalizeLocale(getLanguage())
  if (typeof window !== 'undefined' && window.location?.pathname === '/join') {
    return pageLanguage
  }
  return normalizeLocale(getCookie(LanguageKey) || pageLanguage)
}

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 浏览器复用原券商 API；隔离 App Cookie，登录态只从已验证的 opener 消息获取。
    if (isBrokerWebAuthorization()) {
      const allowedPath = /^\/brokers\/(futu\/auth-links(?:\/[^/]+\/(?:status|captcha))?|longbridge\/credentials|usmart\/credentials)$/.test(config.url || '')
      const token = getBrowserBrokerToken()
      if (!allowedPath || !token || !brokerWebContext.ready || config.method === 'delete') throw brokerWebError()
      config.brokerWebAuthorization = true
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${token}`
      config.headers['X-Platform'] = 'web'
      config.headers['X-Language'] = brokerWebContext.lang || 'US'
      config.headers['X-Device-Id'] = brokerWebContext.deviceId
      config.headers['X-DeviceInfo'] = brokerWebContext.deviceInfo
      return config
    }
    // 在发送请求之前做些什么
    const token = getCookie(TokenKey)
    const deviceId = getCookie(DeviceIdKey)
    const language = getRequestLanguage()
    const deviceInfo = getCookie(DeviceInfoKey)
    const clientPlatform = getCookie(ClientPlatformKey)

    console.log(`[axios] 请求 ${config.method?.toUpperCase()} ${config.url}`, {
      hasToken: !!token,
      tokenPreview: token ? `${token.slice(0, 8)}...(${token.length}字符)` : '空'
    })

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    if (deviceId && config.headers) {
      config.headers['X-Device-Id'] = deviceId
    }

    if (config.headers) {
      config.headers['X-Language'] = language
    }
    
    if (deviceInfo && config.headers) {
      config.headers['X-DeviceInfo'] = deviceInfo
    }

    // AppWeb iframe 下发的是 web 登录槽位 token，必须携带 X-Platform 才能通过后端分槽校验。
    if (clientPlatform && config.headers) {
      config.headers['X-Platform'] = clientPlatform
    }
    
    // if(config.headers){
    //   config.headers['X-Language'] = getLanguage() || 'US'
    // }
    
    // 动态设置语言参数（使用查询参数避免CORS预检请求）
    const currentLang = language
    if (!config.params) {
      config.params = {}
    }
    config.params.lang = currentLang
    
    return config
  },
  (error) => {
    // 对请求错误做些什么
    return Promise.reject(error)
  }
)

const getHttpMessage = (key, fallback) => {
  const translator = i18n?.global?.t
  if (typeof translator === 'function') {
    const result = translator(key)
    if (result && result !== key) {
      return result
    }
  }
  return fallback
}

const showHttpToast = (key, fallback) => {
  toast.show({
    message: getHttpMessage(key, fallback),
    position: 'toast-center'
  })
}

let isAuthDialogVisible = false

const handleAuthExpired = async () => {
  if (isAuthDialogVisible) {
    return
  }
  isAuthDialogVisible = true
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem('token')
    }
    removeCookie(TokenKey)
    await JSBridge.navigateToApp('/login', { requireLogout: true })
  } catch (error) {
    console.warn('navigateToApp login failed:', error)
  } finally {
    isAuthDialogVisible = false
  }
}

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    const { data, config } = response
    // 受限失败不触发 App 登录跳转，也不输出敏感请求配置。
    if (config?.brokerWebAuthorization) {
      if (data.code === 200) return data.data
      const error = new Error('brokerWeb.failed')
      error.code = data.code
      return Promise.reject(error)
    }
    // 11001错误码表示token 不存在，11002错误码表示token失效，10010错误表示非法请求，需要重新登录
    if (data.code === 11001||data.code === 11002||data.code === 10010) {
      console.error(`[axios] Token 异常 code=${data.code}, msg=${data.message}, url=${config.url}`)
      handleAuthExpired()
      const tokenErrorMsg = data.message || getHttpMessage('common.http.tokenException', 'Session expired, please sign in again')
      showHttpToast('common.http.tokenException', tokenErrorMsg)
      return Promise.reject(new Error(tokenErrorMsg))
    }

    if (data.code === 200) {
      return data.data
    }

    const errorMsg = data.message || getHttpMessage('common.http.requestError', 'Request error')
    if (!config?.hideErrorToast) {
      toast.show({
        message: errorMsg,
        position: 'toast-center'
      })
    }

    const error = new Error(errorMsg)
    error.code = data.code
    error.payload = data
    return Promise.reject(error)
  },
  (error) => {
    if (error.config?.brokerWebAuthorization || isBrokerWebAuthorization()) return Promise.reject(brokerWebError('authorization_service_unavailable'))
    if (error.response) {
      switch (error.response.status) {
        case 401:
          handleAuthExpired()
          break
        case 403:
          showHttpToast('common.http.noPermission', 'No permission to access')
          break
        case 404:
          showHttpToast('common.http.notFound', 'Resource not found')
          break
        case 500:
          showHttpToast('common.http.serverError', 'Server error')
          break
        default:
          toast.show({
            message: error.message || getHttpMessage('common.http.requestFailed', 'Request failed'),
            position: 'toast-center'
          })
      }
    } else if (error.request) {
      showHttpToast('common.http.timeout', 'Server response timeout')
    } else {
      showHttpToast('common.http.requestFailed', 'Request failed')
    }

    return Promise.reject(error)
  }
)

// 封装通用请求方法
const http = {
  get(url, params, config) {
    return service.get(url, { params, ...config })
  },

  post(url, data, config) {
    return service.post(url, data, config)
  },

  put(url, data, config) {
    return service.put(url, data, config)
  },

  delete(url, params, config) {
    return service.delete(url, { params, ...config })
  },

  upload(url, file, config) {
    const formData = new FormData()
    formData.append('file', file)
    return service.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...config
    })
  }
}

export const requestClient = service
export default http
